import { Source as TaskSourceApi, Status as TaskApiStatus } from '@wix/ambassador-ai-scheduling-assistant-v1-task/types';
import { EnrichedTaskStatus as EnrichedTaskApiStatus } from '@wix/ambassador-asa-enriched-task-v1-enriched-task/types';
import { actionApiToActionEntity } from '../Action';
import { calendarDisplayApiToCalendarDisplay } from '../Calendar';
import { contactDisplayApiToContactDisplay } from '../Contact';
import { serviceDisplayApiToServiceDisplay } from '../Service';
import { isValidEnrichedTask, isValidTask, TaskSource, TaskStatus } from './Task';
const taskApiStatusToTaskStatusMap = {
  [TaskApiStatus.UNKNOWN_STATUS]: TaskStatus.UNKNOWN_STATUS,
  [TaskApiStatus.AWAITING_ANALYSIS]: TaskStatus.AWAITING_ANALYSIS,
  [TaskApiStatus.PENDING_APPROVAL]: TaskStatus.PENDING_APPROVAL,
  [TaskApiStatus.IN_PROGRESS]: TaskStatus.IN_PROGRESS,
  [TaskApiStatus.AWAITING_DETAILS]: TaskStatus.AWAITING_DETAILS,
  [TaskApiStatus.ACTION_NEEDED]: TaskStatus.ACTION_NEEDED,
  [TaskApiStatus.COMPLETE]: TaskStatus.COMPLETE,
  [TaskApiStatus.CANCELED]: TaskStatus.CANCELED,
  [TaskApiStatus.FAILED]: TaskStatus.FAILED
};
const taskStatusApiToTaskStatusMap = {
  [TaskStatus.UNKNOWN_STATUS]: TaskApiStatus.UNKNOWN_STATUS,
  [TaskStatus.AWAITING_ANALYSIS]: TaskApiStatus.AWAITING_ANALYSIS,
  [TaskStatus.PENDING_APPROVAL]: TaskApiStatus.PENDING_APPROVAL,
  [TaskStatus.IN_PROGRESS]: TaskApiStatus.IN_PROGRESS,
  [TaskStatus.AWAITING_DETAILS]: TaskApiStatus.AWAITING_DETAILS,
  [TaskStatus.ACTION_NEEDED]: TaskApiStatus.ACTION_NEEDED,
  [TaskStatus.COMPLETE]: TaskApiStatus.COMPLETE,
  [TaskStatus.CANCELED]: TaskApiStatus.CANCELED,
  [TaskStatus.FAILED]: TaskApiStatus.FAILED
};
const enrichedTaskStatusToTaskApiStatusMap = {
  [EnrichedTaskApiStatus.UNKNOWN_STATUS]: TaskApiStatus.UNKNOWN_STATUS,
  [EnrichedTaskApiStatus.AWAITING_ANALYSIS]: TaskApiStatus.AWAITING_ANALYSIS,
  [EnrichedTaskApiStatus.PENDING_APPROVAL]: TaskApiStatus.PENDING_APPROVAL,
  [EnrichedTaskApiStatus.IN_PROGRESS]: TaskApiStatus.IN_PROGRESS,
  [EnrichedTaskApiStatus.AWAITING_DETAILS]: TaskApiStatus.AWAITING_DETAILS,
  [EnrichedTaskApiStatus.ACTION_NEEDED]: TaskApiStatus.ACTION_NEEDED,
  [EnrichedTaskApiStatus.COMPLETE]: TaskApiStatus.COMPLETE,
  [EnrichedTaskApiStatus.CANCELED]: TaskApiStatus.CANCELED,
  [EnrichedTaskApiStatus.FAILED]: TaskApiStatus.FAILED
};
const taskSourceToTaskSourceApiMap = {
  [TaskSource.UNKNOWN_SOURCE]: TaskSourceApi.UNKNOWN_SOURCE,
  [TaskSource.MANUAL]: TaskSourceApi.MANUAL,
  [TaskSource.CHAT]: TaskSourceApi.CHAT,
  [TaskSource.RECURRING_SCHEDULE]: TaskSourceApi.RECURRING_SCHEDULE,
  [TaskSource.ANONYMOUS_LINK]: TaskSourceApi.ANONYMOUS_LINK,
  [TaskSource.RECOMMENDATION]: TaskSourceApi.RECOMMENDATION
};
const taskSourceApiToTaskSourceMap = {
  [TaskSourceApi.UNKNOWN_SOURCE]: TaskSource.UNKNOWN_SOURCE,
  [TaskSourceApi.MANUAL]: TaskSource.MANUAL,
  [TaskSourceApi.CHAT]: TaskSource.CHAT,
  [TaskSourceApi.RECURRING_SCHEDULE]: TaskSource.RECURRING_SCHEDULE,
  [TaskSourceApi.ANONYMOUS_LINK]: TaskSource.ANONYMOUS_LINK,
  [TaskSourceApi.RECOMMENDATION]: TaskSource.RECOMMENDATION
};
export const taskApiStatusToTaskStatus = status => {
  if (!status || !taskApiStatusToTaskStatusMap[status]) {
    throw new Error(`unknown TaskApiStatus ${status}`);
  }
  return taskApiStatusToTaskStatusMap[status];
};
export const taskStatusToTaskApiStatus = status => {
  return taskStatusApiToTaskStatusMap[status];
};
export const enrichedTaskApiStatusToTaskApiStatus = status => {
  if (!status || !enrichedTaskStatusToTaskApiStatusMap[status]) {
    throw new Error(`unknown EnrichedTaskApiStatus ${status}`);
  }
  return enrichedTaskStatusToTaskApiStatusMap[status];
};
export const taskSourceApiToTaskSource = source => {
  if (!source || !taskSourceApiToTaskSourceMap[source]) {
    throw new Error(`unknown TaskSourceApi ${source}`);
  }
  return taskSourceApiToTaskSourceMap[source];
};
export const taskSourceToTaskSourceApi = source => {
  return taskSourceToTaskSourceApiMap[source];
};
const getTaskStatusMessage = task => {
  switch (task.status) {
    case TaskApiStatus.AWAITING_DETAILS:
      return task.awaitingDetailsOptions.message;
    case TaskApiStatus.ACTION_NEEDED:
      return task.actionNeededOptions.message;
    default:
      return undefined;
  }
};
export const taskStatusAndMessageToApiStatusAndDetails = (status, statusMessage) => {
  switch (status) {
    case TaskStatus.AWAITING_DETAILS:
      return {
        status: taskStatusToTaskApiStatus(status),
        awaitingDetailsOptions: {
          message: statusMessage
        },
        fieldMask: ['status', 'awaitingDetailsOptions']
      };
    case TaskStatus.ACTION_NEEDED:
      return {
        status: taskStatusToTaskApiStatus(status),
        actionNeededOptions: {
          message: statusMessage
        },
        fieldMask: ['status', 'actionNeededOptions']
      };
    default:
      return {
        status: taskStatusToTaskApiStatus(status),
        fieldMask: ['status']
      };
  }
};
export const taskApiToTaskEntity = task => {
  if (!task) {
    throw new Error('Invalid task', {
      cause: {
        task
      }
    });
  }
  const convertedTask = {
    id: task.id,
    revision: task.revision,
    createdDate: task.createdDate,
    updatedDate: task.updatedDate,
    description: task.description,
    source: taskSourceApiToTaskSource(task.source),
    status: taskApiStatusToTaskStatus(task.status),
    guidelines: task.guidelines,
    createdBy: task.createdBy,
    createdByIdentity: task.createdByIdentity,
    statusMessage: getTaskStatusMessage(task)
  };
  if (isValidTask(convertedTask)) {
    return convertedTask;
  }
  throw new Error('Invalid task data', {
    cause: {
      convertedTask
    }
  });
};
export const enrichedTaskApiToEnrichedEntity = enrichedTaskApi => {
  var _enrichedTaskApi$acti, _enrichedTaskApi$serv, _enrichedTaskApi$cont, _enrichedTaskApi$cale;
  if (!enrichedTaskApi) {
    throw new Error('Invalid enriched enrichedTaskApi', {
      cause: {
        enrichedTaskApi
      }
    });
  }
  const convertedEnrichedTask = {
    ...taskApiToTaskEntity(enrichedTaskApi),
    createdByDetails: enrichedTaskApi.createdByDetails,
    actions: ((_enrichedTaskApi$acti = enrichedTaskApi.actions) == null ? void 0 : _enrichedTaskApi$acti.map(actionApiToActionEntity)) || [],
    services: ((_enrichedTaskApi$serv = enrichedTaskApi.services) == null ? void 0 : _enrichedTaskApi$serv.map(serviceDisplayApiToServiceDisplay)) || [],
    contacts: ((_enrichedTaskApi$cont = enrichedTaskApi.contacts) == null ? void 0 : _enrichedTaskApi$cont.map(contactDisplayApiToContactDisplay)) || [],
    calendars: ((_enrichedTaskApi$cale = enrichedTaskApi.calendars) == null ? void 0 : _enrichedTaskApi$cale.map(calendarDisplayApiToCalendarDisplay)) || []
  };
  if (isValidEnrichedTask(convertedEnrichedTask)) {
    return convertedEnrichedTask;
  }
  throw new Error('Invalid enriched task data', {
    cause: {
      convertedTask: convertedEnrichedTask
    }
  });
};
//# sourceMappingURL=converters.js.map